/**
 * CardCheckoutPanel - the Stripe half of card checkout (P5-D17-F9 §4, T-F9-5).
 *
 * Owns: intent creation ONCE per client attempt (Idempotency-Key lifecycle), the
 * reservation-bound `sessionStorage` carrier WRITE (Decision 2), the Stripe.js
 * mount (memoized `getStripe`, A1), the Express Checkout Element (wallets) + the
 * Payment Element (themed via the Appearance API; saved card renders NATIVELY
 * inside it — B7, no bespoke row), B12 completeness reporting, `confirmPayment`,
 * the B9 decline mapping, and the B11 `expiresAt` handoff.
 *
 * It ENDS where `confirmPayment` resolves: success → `onPaymentSubmitted`
 * (T-F9-6's poll entry); the poll/overlay machinery is NOT here.
 *
 * Structure: the outer panel creates the intent and renders `<Elements>`; the
 * inner `CardCheckoutForm` (inside Elements, so it can use `useStripe`/
 * `useElements`) owns the confirm. The outer bridges the imperative `submit()`
 * (page Pay button, B12) to the inner via a ref; the inner's own ECE `confirm`
 * event runs the SAME confirm path (decision 5: one submit in flight).
 */

import {
  forwardRef,
  useContext,
  useImperativeHandle,
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
  type ReactNode,
} from 'react';
import { Elements, ExpressCheckoutElement, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { getStripe } from '@/lib/stripe';
import { lookAppearance, elementsFonts, paymentElementLayout } from './stripeAppearance';
import { useCreatePaymentIntent, type PaymentIntentRequest } from '@/hooks/useCreatePaymentIntent';
import { generateIdempotencyKey } from '@/lib/idempotency';
import type { CheckoutError } from '@/hooks/checkoutError';
import type { CreatePaymentIntentResponse } from '@/api/types';
import { checkoutEntryIdentity, type CheckoutEntry } from '@/types/checkout';
import { CardPaymentWait } from './cardPaymentWait';
import styles from './CardCheckoutPanel.module.css';

/** sessionStorage carrier key (Decision 2) — bound to the checkout entry identity
 *  (flow token OR postApproval bookingId; booking-detail-reskin D3). */
export const carrierKey = (entry: CheckoutEntry) =>
  `telify:lastPaymentIntent:${checkoutEntryIdentity(entry)}`;

/** Result of an imperative card submit. */
export interface CardSubmitOutcome {
  status: 'succeeded' | 'error';
  stripePaymentIntentId?: string;
  error?: CheckoutError;
}

export interface CardCheckoutPanelHandle {
  /** Driven by the page's bottom Pay button (B12). */
  submit: () => Promise<CardSubmitOutcome>;
}

/** A user-safe decline for the page's B9 banner (Frame 6). */
export interface CardDecline {
  title: string;
}

export interface CardCheckoutPanelProps {
  /** The checkout entry — flow (flowToken + optional recipientToken) OR postApproval (bookingId). */
  entry: CheckoutEntry;
  /** Reports Payment Element completeness to gate the bottom Pay button (B12). */
  onCompleteChange?: (complete: boolean) => void;
  /** Intent `expiresAt` for the page's B11 timer re-sync (null when none). */
  onIntentReady?: (expiresAt: string | null) => void;
  /** Submit-in-flight (covers Pay AND the ECE confirm) — page disables Pay. */
  onSubmitInFlightChange?: (inFlight: boolean) => void;
  /** Fired after confirmPayment resolves successfully — T-F9-6's poll entry. */
  onPaymentSubmitted?: (payload: { stripePaymentIntentId: string }) => void;
  /** PAYMENT_COMPLETED_CONFLICT from intent create routes here (T-F9-6). */
  onConflict?: () => void;
  /** B9 decline → the page renders the banner above Pay (null clears it). */
  onDecline?: (decline: CardDecline | null) => void;
  /** Intent-create errors the page must act on (CARD_UNAVAILABLE/HOLD_EXPIRED → refetch). */
  onError?: (error: CheckoutError) => void;
  /** A card field (inside the Stripe iframe) gained focus — passes the Payment Element's
   *  host node so the page can keyboard-scroll the card surface into view. The inner input
   *  is cross-origin, so this element is the finest-grained centering target available. */
  onCardFieldFocus?: (el: HTMLElement) => void;
  /** A card field lost focus (mirror of onCardFieldFocus). */
  onCardFieldBlur?: () => void;
  /** The intent transport (shared-client-screens D2): the app passes appPaymentIntentRequest, the website its own session's. */
  intentRequest: PaymentIntentRequest;
}

const NOT_READY: CardSubmitOutcome = {
  status: 'error',
  error: { message: 'Card form is not ready yet.', code: 'NOT_READY', canRetry: true },
};

/** Map a Stripe confirm error to a user-safe decline (never raw codes). */
function mapStripeDecline(error: { type?: string; code?: string; decline_code?: string; message?: string }): CardDecline {
  // Stripe's own words for this one are written for developers ("If the customer is off session, ask them to
  // return to your application..."), so the client reads plain words (Gabriel's 2026-11-02 walk).
  if (error.code === 'authentication_required' || error.decline_code === 'authentication_required') {
    return { title: 'Your bank needs to confirm this payment. Try again, or use another card.' };
  }
  if ((error.type === 'card_error' || error.type === 'validation_error') && error.message) {
    return { title: error.message };
  }
  return { title: 'Your card was declined' };
}

// ============================================
// Inner form (inside <Elements> — can use useStripe/useElements)
// ============================================

interface CardCheckoutFormProps {
  stripePaymentIntentId: string;
  onCompleteChange?: (complete: boolean) => void;
  onSubmitInFlightChange?: (inFlight: boolean) => void;
  onPaymentSubmitted?: (payload: { stripePaymentIntentId: string }) => void;
  onDecline?: (decline: CardDecline | null) => void;
  /** Card-field focus/blur (Stripe iframe) → the page's keyboard-aware scroll. */
  onCardFieldFocus?: (el: HTMLElement) => void;
  onCardFieldBlur?: () => void;
  /** Fired when the Payment Element has fully rendered (one half of the reveal gate). */
  onPaymentReady?: () => void;
  /** Fired when the Express Checkout element has resolved wallet availability (the
   *  other half of the reveal gate) — fires even when no wallet is available. */
  onExpressReady?: () => void;
  /** Hands the confirm fn up to the outer panel's imperative submit(). */
  registerConfirm: (fn: () => Promise<CardSubmitOutcome>) => void;
}

function CardCheckoutForm({
  stripePaymentIntentId,
  onCompleteChange,
  onSubmitInFlightChange,
  onPaymentSubmitted,
  onDecline,
  onCardFieldFocus,
  onCardFieldBlur,
  onPaymentReady,
  onExpressReady,
  registerConfirm,
}: CardCheckoutFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const waiting = useContext(CardPaymentWait);
  const inFlightRef = useRef(false);
  // Host node for the Payment Element — the centering target the page scrolls above the
  // keyboard on card-field focus (the inner input is cross-origin, so this is as fine as
  // we can point).
  const paymentHostRef = useRef<HTMLDivElement>(null);
  // Only show the wallet slot's "or pay with card" divider when a wallet actually
  // renders (mockup State 2: no wallet ⇒ no Express element, no divider). The Express
  // element stays mounted so its onReady can report wallet availability.
  const [hasWallet, setHasWallet] = useState(false);

  const runConfirm = useCallback(async (): Promise<CardSubmitOutcome> => {
    if (!stripe || !elements || inFlightRef.current || waiting) return NOT_READY;

    inFlightRef.current = true;
    onSubmitInFlightChange?.(true);
    onDecline?.(null); // clear any prior decline — retry in place

    try {
      const result = await stripe.confirmPayment({
        elements,
        // return_url is Stripe-required even with redirect:'if_required' (NOT the cut A10 fallback).
        confirmParams: { return_url: window.location.href },
        redirect: 'if_required',
      });

      if (result && 'error' in result && result.error) {
        const decline = mapStripeDecline(result.error);
        onDecline?.(decline);
        return { status: 'error', error: { message: decline.title, code: 'CARD_DECLINED', canRetry: true } };
      }

      // Any non-error resolution (succeeded / processing / 3DS handled in-context)
      // hands off to the poll — T-F9-6 owns everything after.
      onPaymentSubmitted?.({ stripePaymentIntentId });
      return { status: 'succeeded', stripePaymentIntentId };
    } finally {
      inFlightRef.current = false;
      onSubmitInFlightChange?.(false);
    }
  }, [stripe, elements, waiting, stripePaymentIntentId, onSubmitInFlightChange, onDecline, onPaymentSubmitted]);

  // Bridge confirm up to the outer panel's submit().
  useEffect(() => {
    registerConfirm(runConfirm);
  }, [registerConfirm, runConfirm]);

  return (
    <>
      {/* While the number is asked for, the wallet button takes no tap, and a confirm that still arrives is turned down. */}
      <div className={waiting ? `${styles.expressSlot} ${styles.expressWaiting}` : styles.expressSlot} aria-disabled={waiting || undefined}>
        <ExpressCheckoutElement
          onConfirm={(event) => {
            if (waiting) {
              event.paymentFailed?.();
              return;
            }
            void runConfirm();
          }}
          // `ready` fires even with no wallet (availablePaymentMethods undefined), so
          // by reveal time we know definitively whether the wallet row exists — the
          // divider is settled BEFORE the reveal, never popped in after.
          onReady={({ availablePaymentMethods }) => {
            setHasWallet(!!availablePaymentMethods);
            onExpressReady?.();
          }}
          options={{}}
        />
      </div>
      {hasWallet && <div className={styles.orDiv}>or pay with card</div>}
      <div ref={paymentHostRef}>
        <PaymentElement
          options={{ layout: paymentElementLayout }}
          onReady={() => onPaymentReady?.()}
          onChange={(event) => onCompleteChange?.(event.complete)}
          // Cross-origin card fields never surface a DOM `focusin`; Stripe's own focus
          // events are the only signal, so route them to the page's keyboard-aware scroll.
          onFocus={() => {
            if (paymentHostRef.current) onCardFieldFocus?.(paymentHostRef.current);
          }}
          onBlur={() => onCardFieldBlur?.()}
        />
      </div>
    </>
  );
}

/**
 * Placeholder shown until BOTH Stripe elements report ready. It echoes the full
 * payment surface — a wallet button (Express Checkout), the "or pay with card"
 * divider, and the single Card accordion row (Link is disabled account-side, so no
 * Bank row) — so the whole surface reveals in ONE shift-free transition with no
 * late wallet pop-in. Tradeoff: in a wallet-less context (no Apple/Google Pay) the
 * wallet+divider rows resolve away at reveal (a small upward collapse); accepted
 * because the wallet case is the common one and the alternative is the pop-in jank.
 */
function CardSkeleton() {
  return (
    <div className={styles.skeleton} data-testid="card-skeleton" aria-hidden="true">
      <div className={styles.skelWallet} />
      <div className={styles.skelDivider}>
        <span className={styles.skelDividerBar} />
      </div>
      <div className={styles.skelRow}>
        <span className={styles.skelRadio} />
        <span className={styles.skelBar} />
      </div>
    </div>
  );
}

// ============================================
// Outer panel
// ============================================

export const CardCheckoutPanel = forwardRef<CardCheckoutPanelHandle, CardCheckoutPanelProps>(
  function CardCheckoutPanel(props, ref) {
    const {
      entry,
      onCompleteChange,
      onIntentReady,
      onSubmitInFlightChange,
      onPaymentSubmitted,
      onConflict,
      onDecline,
      onError,
      onCardFieldFocus,
      onCardFieldBlur,
      intentRequest,
    } = props;

    const [intent, setIntent] = useState<CreatePaymentIntentResponse | null>(null);
    const [intentError, setIntentError] = useState<CheckoutError | null>(null);
    // Reveal gate: drop our skeleton and show the whole surface at once only after
    // BOTH elements report ready — the Payment Element (card accordion) AND the
    // Express Checkout element (wallets). Gating on both means the wallet row +
    // divider are settled before the reveal (no late pop-in), and Stripe's own
    // in-iframe loaders never flash through.
    const [paymentReady, setPaymentReady] = useState(false);
    const [expressReady, setExpressReady] = useState(false);
    const surfaceReady = paymentReady && expressReady;

    // Intent-once guards (per-instance — a new attempt = a fresh panel mount).
    const startedRef = useRef(false);
    const keyRef = useRef<string>('');
    const inProgressRetriedRef = useRef(false);
    const confirmRef = useRef<(() => Promise<CardSubmitOutcome>) | null>(null);
    // Latest-mutate ref so the retry can re-fire without a self-reference in the
    // hook options (avoids use-before-define on `createIntent`).
    const fireIntentRef = useRef<() => void>(() => {});

    const createIntent = useCreatePaymentIntent({
      request: intentRequest,
      onSuccess: (response) => {
        setIntentError(null);
        setIntent(response);
        // Carrier WRITE (Decision 2) — the full reservation-bound triple; overwrite-latest.
        try {
          sessionStorage.setItem(
            carrierKey(entry),
            JSON.stringify({
              reservationKind: response.reservationKind,
              reservationId: response.reservationId,
              stripePaymentIntentId: response.stripePaymentIntentId,
            }),
          );
        } catch {
          /* sessionStorage unavailable — non-fatal; component state still holds the intent */
        }
        onIntentReady?.(response.expiresAt);
      },
      onError: (error) => {
        switch (error.code) {
          case 'PAYMENT_COMPLETED_CONFLICT':
          // studio-shop D6: another tab's payment for the order went through.
          case 'ORDER_PAID':
            onConflict?.();
            return;
          case 'CARD_UNAVAILABLE':
          case 'HOLD_EXPIRED':
            // Page refetches flowContext → the §3 router reroutes (degrade/hard-block);
            // HOLD_EXPIRED also fires the global hold-expiry guard inside apiFetch.
            onError?.(error);
            return;
          case 'PAYMENT_WINDOW_TOO_CLOSE':
            // CWR 2.6 (S7): the slot is too close to its start to pay — the PAGE owns
            // the page-level "Pick a new time" expired state, so bubble up rather than
            // rendering it as the panel's inline intentError.
            onError?.(error);
            return;
          case 'ORDER_ABANDONED':
            // studio-shop D6: the sweep abandoned the unpaid order; the checkout starts a new one at today's prices.
            onError?.(error);
            return;
          case 'FORMS_OUTSTANDING':
            // Forms are owed after all (added since the page read them): the page reads again and
            // routes to them; the inline error stays in case it does not (Codex review 2026-10-26).
            onError?.(error);
            setIntentError(error);
            return;
          case 'PHONE_NUMBER_REQUIRED':
            // service-by-phone D5: no number on file after all. The page reads again and asks for
            // one; the payment starts over once it is saved.
            startedRef.current = false;
            onError?.(error);
            setIntentError(error);
            return;
          case 'PAYMENT_INTENT_IN_PROGRESS':
          // studio-shop D6: Stripe is processing the order's payment (another tab paid); a retry reads paid or still processing.
          case 'PAYMENT_IN_PROGRESS':
            if (!inProgressRetriedRef.current) {
              inProgressRetriedRef.current = true;
              // One auto-retry ~1.5s, replaying the SAME idempotency key.
              setTimeout(() => fireIntentRef.current(), 1500);
              return;
            }
            setIntentError(error);
            return;
          default:
            // Post-retry IN_PROGRESS / PRICE_MISSING / generic → inline body error.
            setIntentError(error);
        }
      },
    });

    // Keep the fire-intent closure current (replays the SAME idempotency key).
    fireIntentRef.current = () => {
      createIntent.mutate({ ...entry, idempotencyKey: keyRef.current });
    };

    // Create the intent ONCE on activation. The ref guard survives re-renders +
    // StrictMode's double-invoke (same instance, same ref). A genuine new attempt
    // is a fresh panel mount (new instance → new key → superseding intent).
    // service-by-phone D5: activation waits for the number to call.
    const waiting = useContext(CardPaymentWait);
    useEffect(() => {
      if (startedRef.current || waiting) return;
      startedRef.current = true;
      keyRef.current = generateIdempotencyKey();
      setIntentError(null);
      fireIntentRef.current();
    }, [waiting]);

    // Memoize the Stripe instance by (pk, connected account) — only after the response.
    const stripePromise = useMemo(
      () => (intent ? getStripe(intent.publishableKey, intent.stripeAccountId) : null),
      [intent],
    );
    // The studio's look, read once per mount (the iframe can't read the page's tokens).
    const appearance = useMemo(() => lookAppearance(), []);

    useImperativeHandle(
      ref,
      () => ({
        submit: () => (confirmRef.current ? confirmRef.current() : Promise.resolve(NOT_READY)),
      }),
      [],
    );

    let body: ReactNode;
    if (intentError) {
      body = (
        <div className={styles.intentError} role="alert">
          <div className={styles.intentErrorTitle}>{intentErrorTitle(intentError)}</div>
          {intentErrorBody(intentError)}
        </div>
      );
    } else if (!intent || !stripePromise) {
      body = <CardSkeleton />;
    } else {
      // Mount both elements immediately but keep them hidden (absolute, opacity 0 —
      // NOT unmounted, so Stripe still renders + fires its ready events) under our
      // skeleton until BOTH report ready. One placeholder phase, then a single
      // shift-free reveal of the whole surface.
      body = (
        <div className={styles.elementsHost}>
          {!surfaceReady && <CardSkeleton />}
          <div className={surfaceReady ? undefined : styles.elementsHidden}>
            <Elements
              stripe={stripePromise}
              options={{
                clientSecret: intent.clientSecret,
                // A shop guest has no Customer (studio-shop D6): no saved cards, no save box.
                customerSessionClientSecret: intent.customerSessionClientSecret ?? undefined,
                appearance,
                fonts: elementsFonts,
              }}
            >
              <CardCheckoutForm
                stripePaymentIntentId={intent.stripePaymentIntentId}
                onCompleteChange={onCompleteChange}
                onSubmitInFlightChange={onSubmitInFlightChange}
                onPaymentSubmitted={onPaymentSubmitted}
                onDecline={onDecline}
                onCardFieldFocus={onCardFieldFocus}
                onCardFieldBlur={onCardFieldBlur}
                onPaymentReady={() => setPaymentReady(true)}
                onExpressReady={() => setExpressReady(true)}
                registerConfirm={(fn) => {
                  confirmRef.current = fn;
                }}
              />
            </Elements>
          </div>
        </div>
      );
    }

    return <div data-testid="card-checkout-panel">{body}</div>;
  },
);

function intentErrorTitle(error: CheckoutError): string {
  if (error.code === 'PAYMENT_WINDOW_TOO_CLOSE') return "Not enough time left to pay";
  return "Couldn't start card checkout";
}

function intentErrorBody(error: CheckoutError): string {
  if (error.code === 'PAYMENT_WINDOW_TOO_CLOSE') {
    return "There isn't enough time left on this slot to pay. Please go back and pick a new time.";
  }
  return error.message;
}
